import { useEffect, useRef, useState } from 'react';

export default function AboutSection() {
  const [isVisible, setIsVisible] = useState(false);
  const [counts, setCounts] = useState({ members: 0, vehicles: 0, competitions: 0 });
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (isVisible) {
      const duration = 2000;
      const steps = 60;
      const interval = duration / steps;

      const targets = { members: 40, vehicles: 5, competitions: 10 };
      let step = 0;

      const timer = setInterval(() => {
        step++;
        const progress = step / steps;
        setCounts({
          members: Math.floor(targets.members * progress),
          vehicles: Math.floor(targets.vehicles * progress),
          competitions: Math.floor(targets.competitions * progress)
        });

        if (step >= steps) {
          clearInterval(timer);
          setCounts(targets);
        }
      }, interval);

      return () => clearInterval(timer);
    }
  }, [isVisible]);

  const stats = [
    { value: counts.members, label: 'Aktif Üye', icon: 'ri-team-line' },
    { value: counts.vehicles, label: 'Üretilen Araç', icon: 'ri-car-line' },
    { value: counts.competitions, label: 'Yarışma Katılımı', icon: 'ri-trophy-line' }
  ];

  const values = [
    { icon: 'ri-lightbulb-flash-line', title: 'İnovasyon', desc: 'Sınırları zorlayan çözümler' },
    { icon: 'ri-team-line', title: 'Takım Ruhu', desc: 'Birlikte başarıyoruz' },
    { icon: 'ri-star-line', title: 'Mükemmellik', desc: 'Her detayda kalite' },
    { icon: 'ri-leaf-line', title: 'Sürdürülebilirlik', desc: 'Geleceğe saygılı mühendislik' }
  ];

  return (
    <section id="hakkımızda" ref={sectionRef} className="relative bg-[#1a1a1a] py-32 px-8">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center mb-24">
          {/* Left Content */}
          <div className={`space-y-8 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}>
            <div className="space-y-4">
              <h3 className="text-[#00d9ff] text-sm uppercase tracking-[0.3em] font-semibold">
                TETRA KİMDİR?
              </h3>
              <h2 className="text-6xl font-bold leading-tight">
                Geleceği İnşa Eden Mühendisler
              </h2>
            </div>

            <div className="space-y-6 text-lg text-white/70 leading-relaxed">
              <p>
                TETRA, üniversite öğrencilerinden oluşan tutkulu bir mühendislik takımıdır. 
                Yarış araçları tasarlayıp üreterek, uluslararası yarışmalarda ülkemizi temsil ediyoruz.
              </p>
              <p>
                Mekanik, elektronik, yazılım ve tasarım alanlarında uzmanlaşan üyelerimiz, 
                her yıl yeni bir araç projesi geliştirerek mühendislik becerilerini geliştiriyor.
              </p>
              <p>
                Formula Student, robotik yarışmalar ve otomotiv teknolojileri alanında 
                edindiğimiz deneyimlerle, geleceğin mühendislerini yetiştiriyoruz.
              </p>
            </div>
          </div>

          {/* Right - Stats Cards */}
          <div className="space-y-6">
            {stats.map((stat, index) => (
              <div
                key={index}
                className={`group bg-[#0f0f0f] border border-[#00d9ff]/30 rounded-lg p-8 flex items-center justify-between transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_10px_40px_rgba(0,217,255,0.2)] cursor-pointer ${
                  isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'
                }`}
                style={{ transitionDelay: `${index * 0.2}s` }}
              >
                <div className="flex items-center gap-6">
                  <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-[#00d9ff] to-[#0088cc] rounded-lg group-hover:scale-110 transition-transform duration-300">
                    <i className={`${stat.icon} text-3xl text-black`} />
                  </div>
                  <div>
                    <div className="text-5xl font-bold bg-gradient-to-r from-white to-[#00d9ff] bg-clip-text text-transparent">
                      {stat.value}+
                    </div>
                    <div className="text-lg text-white/60 mt-1">{stat.label}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Values Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {values.map((value, index) => (
            <div
              key={index}
              className={`group text-center space-y-4 p-8 rounded-lg bg-[#0f0f0f]/50 border border-transparent hover:border-[#ff6b35]/50 transition-all duration-500 hover:-translate-y-2 cursor-pointer ${
                isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
              }`}
              style={{ transitionDelay: `${index * 0.1 + 0.6}s` }}
            >
              <div className="w-16 h-16 mx-auto flex items-center justify-center">
                <i className={`${value.icon} text-5xl text-[#ff6b35] group-hover:scale-110 transition-transform duration-300`} />
              </div>
              <h4 className="text-xl font-bold text-white">{value.title}</h4>
              <p className="text-white/60 text-sm">{value.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
